import { ref, computed } from 'vue';
import bondService from '@/service/bondService';

export function useCreditBonds() {
  // State
  const issuersData = ref({});
  const groupsByType = ref({});
  const bondTypes = ref([]); // 동적으로 로드될 타입들
  const expandedIssuers = ref([]);
  const isLoading = ref(false);
  const isRefreshing = ref(false);
  const currentType = ref('');

  // 기본 크레딧 타입 (fallback) - 백엔드 CreditType enum과 일치
  const defaultBondTypes = [
    { type: 'PUBLIC', title: '공사채' },
    { type: 'BANK', title: '은행채' },
    { type: 'CREDIT_FINANCE', title: '여전채' },
    { type: 'COMPANY', title: '회사채' },
  ];

  // Computed
  const activeBondTypes = computed(() => {
    return bondTypes.value.length > 0 ? bondTypes.value : defaultBondTypes;
  });

  const typeNames = computed(() => {
    const names = {};
    activeBondTypes.value.forEach(bondType => {
      names[bondType.type] = bondType.title;
    });
    return names;
  });

  const sortedIssuers = computed(() => {
    return (
      issuersData.value[currentType.value]?.sort((a, b) => {
        return a.name.localeCompare(b.name, 'ko-KR');
      }) || []
    );
  });

  // Methods
  const initializeDataStructure = () => {
    const newIssuersData = {};
    const newGroupsByType = {};

    activeBondTypes.value.forEach(bondType => {
      newIssuersData[bondType.type] = issuersData.value[bondType.type] || [];
      newGroupsByType[bondType.type] = groupsByType.value[bondType.type] || [];
    });

    issuersData.value = newIssuersData;
    groupsByType.value = newGroupsByType;

    // currentType 초기화
    if (!currentType.value && activeBondTypes.value.length > 0) {
      currentType.value = activeBondTypes.value[0].type;
    }
  };

  const getColumnTitle = type => {
    return typeNames.value[type] || type;
  };

  const loadBondTypes = async () => {
    try {
      const response = await bondService.getCreditBondTypes();
      bondTypes.value = response || [];

      // 데이터 구조 초기화
      initializeDataStructure();
    } catch (error) {
      console.error('크레딧 타입 로드 실패:', error);
      // 에러 시 기본값 사용
      bondTypes.value = [];
      initializeDataStructure();
    }
  };

  const loadCreditData = async () => {
    try {
      isLoading.value = true;

      // 타입을 먼저 로드하고 데이터 구조 초기화
      await loadBondTypes();

      // 발행사 데이터와 그룹 데이터를 병렬로 로드
      await Promise.all([loadIssuersData(), loadGroups()]);
    } catch (error) {
      console.error('크레딧 데이터 로드 실패:', error);
    } finally {
      isLoading.value = false;
    }
  };

  const loadIssuersData = async () => {
    const response = await bondService.getCreditIssuers();

    // BondIssuerResponse 구조에 맞게 데이터 처리
    const issuerMap = {
      PUBLIC: response._public || [],
      BANK: response.bank || [],
      CREDIT_FINANCE: response.creditFinance || [],
      COMPANY: response.company || [],
    };

    // 타입별로 데이터 분류
    activeBondTypes.value.forEach(bondType => {
      // API 응답의 키와 bondType.type 매핑
      const mappedKey = mapCreditTypeToResponseKey(bondType.type);
      issuersData.value[bondType.type] = issuerMap[mappedKey] || [];
    });
  };

  const mapCreditTypeToResponseKey = type => {
    const mapping = {
      PUBLIC: 'PUBLIC',
      BANK: 'BANK',
      CREDIT_FINANCE: 'CREDIT_FINANCE',
      COMPANY: 'COMPANY',
    };
    return mapping[type] || type;
  };

  const loadGroups = async (specificType = null) => {
    try {
      // 특정 타입이 지정된 경우 해당 타입만, 아니면 모든 타입 로드
      const typesToLoad = specificType 
        ? activeBondTypes.value.filter(bt => bt.type === specificType)
        : activeBondTypes.value;

      // 각 크레딧 타입별로 그룹을 병렬로 조회
      const groupPromises = typesToLoad.map(async bondType => {
        try {
          const response = await bondService.getBondGroups(bondType.type);
          groupsByType.value[bondType.type] = response || [];
        } catch (error) {
          console.error(`${bondType.type} 그룹 로드 실패:`, error);
          groupsByType.value[bondType.type] = [];
        }
      });

      await Promise.all(groupPromises);
    } catch (error) {
      console.error('그룹 로드 실패:', error);
    }
  };

  const createIssuer = async issuerData => {
    try {
      isLoading.value = true;
      await bondService.createIssuer(issuerData);
      // 발행사 생성 후 전체 데이터를 다시 로드하여 최신 상태 유지
      await loadCreditData();
      return { success: true };
    } catch (error) {
      console.error('발행사 생성 실패:', error);
      throw new Error('발행사 생성 중 오류가 발생했습니다.');
    } finally {
      isLoading.value = false;
    }
  };

  const updateIssuer = async (issuerId, issuerData) => {
    try {
      isLoading.value = true;
      await bondService.updateIssuer(
        issuerId,
        issuerData
      );

      // 발행사 수정 후 전체 데이터를 다시 로드하여 최신 상태 유지
      await loadCreditData();

      return { success: true };
    } catch (error) {
      console.error('발행사 수정 실패:', error);
      throw new Error('발행사 수정 중 오류가 발생했습니다.');
    } finally {
      isLoading.value = false;
    }
  };

  const createAlias = async (issuerId, aliasName) => {
    try {
      await bondService.createAlias(issuerId, aliasName);

      // 별칭 생성 후 전체 데이터를 다시 로드하여 최신 상태 유지
      await loadCreditData();

      return { success: true };
    } catch (error) {
      console.error('별칭 생성 실패:', error);
      throw new Error('별칭 생성 중 오류가 발생했습니다.');
    }
  };

  const deleteAlias = async (issuerId, aliasId) => {
    try {
      await bondService.deleteAlias(aliasId);

      // 별칭 삭제 후 전체 데이터를 다시 로드하여 최신 상태 유지
      await loadCreditData();
    } catch (error) {
      console.error('별칭 삭제 실패:', error);
      throw new Error('별칭 삭제 중 오류가 발생했습니다.');
    }
  };

  const createGroup = async groupData => {
    try {
      const newGroup = await bondService.createCreditGroup(groupData);
      if (!groupsByType.value[groupData.type]) {
        groupsByType.value[groupData.type] = [];
      }
      groupsByType.value[groupData.type].push(newGroup);
      return newGroup;
    } catch (error) {
      console.error('그룹 생성 실패:', error);
      throw new Error('그룹 생성 중 오류가 발생했습니다.');
    }
  };

  const updateGroup = async (groupId, groupData) => {
    try {
      const updatedGroup = await bondService.updateCreditGroup(
        groupId,
        groupData
      );

      // 로컬 데이터 업데이트 - type이 있는 경우와 없는 경우 모두 처리
      if (groupData.type) {
        const groupIndex = groupsByType.value[groupData.type].findIndex(
          g => g.id === groupId || g.groupId === groupId
        );
        if (groupIndex !== -1) {
          groupsByType.value[groupData.type][groupIndex] = updatedGroup;
        }
      } else {
        // type이 없는 경우 모든 타입에서 찾아서 업데이트
        activeBondTypes.value.forEach(bondType => {
          const groupIndex = groupsByType.value[bondType.type].findIndex(
            g => g.id === groupId || g.groupId === groupId
          );
          if (groupIndex !== -1) {
            groupsByType.value[bondType.type][groupIndex] = updatedGroup;
          }
        });
      }

      return updatedGroup;
    } catch (error) {
      console.error('그룹 수정 실패:', error);
      throw new Error('그룹 수정 중 오류가 발생했습니다.');
    }
  };

  const deleteGroup = async groupId => {
    try {
      await bondService.deleteCreditGroup(groupId);

      // 로컬 데이터에서 제거
      activeBondTypes.value.forEach(bondType => {
        if (groupsByType.value[bondType.type]) {
          groupsByType.value[bondType.type] = groupsByType.value[bondType.type].filter(
            g => g.id !== groupId && g.groupId !== groupId
          );
        }
      });
    } catch (error) {
      console.error('그룹 삭제 실패:', error);
      throw new Error('그룹 삭제 중 오류가 발생했습니다.');
    }
  };

  const refreshIssuanceCache = async () => {
    try {
      isRefreshing.value = true;
      await bondService.refreshIssuanceCache();
    } catch (error) {
      console.error('캐시 갱신 실패:', error);
      throw new Error('캐시 갱신 중 오류가 발생했습니다.');
    } finally {
      isRefreshing.value = false;
    }
  };

  const toggleIssuerExpand = issuerId => {
    const index = expandedIssuers.value.indexOf(issuerId);
    if (index > -1) {
      expandedIssuers.value.splice(index, 1);
    } else {
      expandedIssuers.value.push(issuerId);
    }
  };

  return {
    // State
    issuersData,
    groupsByType,
    bondTypes,
    expandedIssuers,
    isLoading,
    isRefreshing,
    currentType,

    // Computed
    activeBondTypes,
    typeNames,
    sortedIssuers,

    // Methods
    getColumnTitle,
    loadBondTypes,
    loadCreditData,
    loadGroups,
    createIssuer,
    updateIssuer,
    createAlias,
    deleteAlias,
    createGroup,
    updateGroup,
    deleteGroup,
    refreshIssuanceCache,
    toggleIssuerExpand,
  };
}
